import { ArrowRight, Download, FileText, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import ShortAnswer from "@/components/ShortAnswer";

const PDF_PATH = "/resources/harassment-risk-assessment-template.pdf";
const DOCX_PATH = "/resources/harassment-risk-assessment-template.docx";
const REVIEW_LINE = "Reviewed by Vanessa Challess, solicitor, qualified 2003. Legal review provided via Bonsai Law.";

export default function ResourceHarassmentTemplate() {
 const origin = window.location.origin;
 const pageUrl = `${origin}/resources/harassment-risk-assessment-template`;

 return (
 <main data-testid="harassment-template-page">
 <Seo
 title="Sexual Harassment Risk Assessment Template (UK) | Electra HR"
 siteName="Electra HR"
 description="Download a free harassment risk assessment template for the duties applying from 30 October 2026, including sexual harassment and third-party harassment. PDF and editable DOCX."
 image="/og/default.jpg"
 jsonLd={{
 "@context": "https://schema.org",
 "@graph": [
 {
 "@type": "WebPage",
 "@id": pageUrl,
 url: pageUrl,
 name: "Harassment risk assessment template",
 description: "A free harassment risk assessment template for UK employers, covering sexual harassment and third-party harassment duties from 30 October 2026.",
 isPartOf: { "@id": `${origin}/#website` },
 publisher: { "@id": `${origin}/#organization` },
 datePublished: "2026-09-07",
 dateModified: "2026-09-07",
 reviewedBy: {
 "@type": "Person",
 name: "Vanessa Challess",
 jobTitle: "Solicitor",
 },
 },
 {
 "@type": "DigitalDocument",
 name: "Harassment risk assessment template",
 url: `${origin}${PDF_PATH}`,
 encodingFormat: "application/pdf",
 dateModified: "2026-09-07",
 author: { "@id": `${origin}/#organization` },
 },
 {
 "@type": "DigitalDocument",
 name: "Harassment risk assessment template, editable DOCX",
 url: `${origin}${DOCX_PATH}`,
 encodingFormat: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
 dateModified: "2026-09-07",
 author: { "@id": `${origin}/#organization` },
 },
 {
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: origin },
 { "@type": "ListItem", position: 2, name: "Resources", item: pageUrl },
 { "@type": "ListItem", position: 3, name: "Harassment risk assessment template", item: pageUrl },
 ],
 },
 ],
 }}
 />

 <section className="grain bg-ink pb-16 pt-32 sm:pt-40 text-paper">
 <div className="container-x max-w-4xl">
 <p className="eyebrow text-pink">Free employer template</p>
 <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
 Harassment risk assessment template
 </h1>
 <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/65">
 A practical risk assessment for the sexual harassment duty and the new third-party harassment duty applying from 30 October 2026. Built for a business owner to complete, not a compliance department.
 </p>
 <p className="mt-5 font-mono text-xs uppercase tracking-[0.18em] text-paper/45">
 6 September 2026
 </p>
 </div>
 </section>

 <section className="bg-paper py-16 sm:py-24">
 <div className="container-x grid gap-10 lg:grid-cols-[1fr_380px]">
 <div>
 <ShortAnswer path="/resources/harassment-risk-assessment-template" className="mb-8" />
 <Reveal>
 <div className="border-l-4 border-pink bg-pink/10 p-6">
 <p className="text-base font-medium leading-relaxed text-ink/80">
 No form, no email gate and no waiting for a link. Download the file, complete it, keep it and act on it. Electra HR does not receive or store completed copies.
 </p>
 </div>
 </Reveal>

 <div className="mt-10 grid gap-5 sm:grid-cols-2">
 <a
 href={PDF_PATH}
 download
 data-testid="download-harassment-template-pdf"
 className="group border-2 border-ink bg-ink p-7 text-paper transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#F472B6]"
 >
 <Download size={24} className="text-pink" />
 <p className="mt-5 font-display text-xl font-semibold">Download the PDF</p>
 <p className="mt-2 text-sm leading-relaxed text-paper/65">Best for printing, sharing internally and keeping a signed-off copy.</p>
 <span className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-pink">
 PDF <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
 </span>
 </a>
 <a
 href={DOCX_PATH}
 download
 data-testid="download-harassment-template-docx"
 className="group border-2 border-ink bg-paper p-7 text-ink transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#0F0F1A]"
 >
 <FileText size={24} className="text-pink" />
 <p className="mt-5 font-display text-xl font-semibold">Download the DOCX</p>
 <p className="mt-2 text-sm leading-relaxed text-ink/65">Editable version for adding your sites, risks, owners, dates and evidence.</p>
 <span className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-ink/50 group-hover:text-ink">
 DOCX <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
 </span>
 </a>
 </div>

 <div className="mt-12 space-y-8">
 <section>
 <h2 className="font-display text-2xl font-semibold tracking-tight">What the template covers</h2>
 <ul className="mt-5 space-y-3 text-base leading-relaxed text-ink/70">
 {[
 "Your existing policy, reporting routes, training, incident log and review arrangements",
 "Risks from customers, clients, contractors, suppliers, visitors and online contact",
 "Lone working, late working, social events, travel and power imbalances",
 "An action plan with owners, target dates, reasons and evidence",
 "An incident log and sign-off section with a dated review point",
 ].map((item) => (
 <li key={item} className="flex gap-3">
 <ShieldCheck size={18} className="mt-1 shrink-0 text-pink" />
 <span>{item}</span>
 </li>
 ))}
 </ul>
 </section>

 <section>
 <h2 className="font-display text-2xl font-semibold tracking-tight">How to use it</h2>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 Work through it in order. For a business of 5 to 50 people, it should take under two hours. Every No in the controls table and every further reasonable step you identify needs an owner and a date. If you decide a step is not reasonable for your business, record that decision and your reason.
 </p>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 The legal background and the changes from 30 October 2026 are explained in our <Link to="/blog/all-reasonable-steps-harassment-30-october-2026" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">harassment duty briefing</Link>.
 </p>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 The assessment should also cover harassment by customers, clients and contractors. What that means for employers from 30 October 2026 is explained in our <Link to="/blog/third-party-harassment-employers-30-october-2026" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">third-party harassment briefing</Link>.
 </p>
 </section>
 </div>
 </div>

 <aside className="h-fit border-2 border-ink bg-pink/10 p-7">
 <p className="eyebrow text-ink/50">Document control</p>
 <p className="mt-4 text-sm leading-relaxed text-ink/75">{REVIEW_LINE}</p>
 <p className="mt-4 text-sm leading-relaxed text-ink/75">
 Electra HR Ltd, company number 11077627. The completed document belongs to the employer. Electra HR does not receive or store completed copies.
 </p>
 <div className="mt-7 border-t border-ink/15 pt-6">
 <p className="font-display text-lg font-semibold">Already dealing with a complaint?</p>
 <p className="mt-2 text-sm leading-relaxed text-ink/65">An assessment is prevention. A live complaint needs a fair process, careful records and early risk triage.</p>
 <Link to="/contact" data-testid="harassment-template-contact-cta" className="group mt-5 inline-flex items-center gap-2 bg-ink px-5 py-3 font-bold text-paper transition-transform duration-300 hover:-translate-y-0.5">
 Talk to us <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
 </Link>
 </div>
 </aside>
 </div>
 </section>
 </main>
 );
}
